import { useQuery } from "@tanstack/react-query"
import { useEffect } from "react"

import useSlugExtractor from "@hooks/useSlugExtractor"
import useUser from "@hooks/useUser"

import toast from "@molecules/Toast/Toast"

import { JOB_SERIES_ROUTES } from "@routes/jobs-series"

export const jobSeriesDetailsQueryKey = (jobSeriesId: string) => ["jobSeriesDetails", jobSeriesId]

export default function useJobSeriesDetails(jobSeriesId: string) {
    const { user } = useUser()
    const [jobSeriesDetailsEndpoint] = useSlugExtractor([JOB_SERIES_ROUTES.DETAILS_API], jobSeriesId)

    const isUserReady = !!user?.service_company?.slug

    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey: [...jobSeriesDetailsQueryKey(jobSeriesId), jobSeriesDetailsEndpoint],
        queryFn: async (): Promise<JobSeries> => {
            const response = await fetch(jobSeriesDetailsEndpoint)
            if (!response.ok) {
                throw new Error("Failed to fetch job series details")
            }
            return (await response.json()) as JobSeries
        },
        enabled: !!jobSeriesId && !!jobSeriesDetailsEndpoint && isUserReady,
    })

    useEffect(() => {
        if (isError) {
            toast({
                type: "error",
                title: "Error loading job series",
                supportText: "Failed to load job series details. Please try again.",
                variant: "detailed",
            })
        }
    }, [isError])

    return {
        jobSeries: data,
        isLoading,
        isError,
        error,
        refetch,
    }
}
